import { useState, useMemo, useCallback } from 'react';
import {
  PoolFilters,
  PoolSortOrder,
  PoolSortBy,
  PoolType,
  LoanType,
  FilterState,
  defaultFilterState,
  toFilterState,
} from '@/types/pool';
import DropDownFilterField from './DropDownFilterField';
import FiltersHeader from './FiltersHeader';
import MinMaxField from './MinMaxField';

type PoolFiltersDropdownProps = {
  onFiltersChange: (filters: PoolFilters) => void;
};

const SORT_ORDER_OPTIONS = [
  { value: PoolSortOrder.DESC, label: 'Newest' },
  { value: PoolSortOrder.ASC, label: 'Oldest' },
];

const POOL_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: PoolType.EQUITY, label: 'Equity' },
  { value: PoolType.REFINANCE, label: 'Refinance' },
];

const POOL_PROGRAM_OPTIONS = [
  { value: '', label: 'All' },
  { value: LoanType.INTEREST_ONLY, label: 'Interest Only' },
  { value: LoanType.AMORTIZING, label: 'Amortizing' },
];

const hasFiltersChangedFrom = (current: FilterState, reference: FilterState): boolean => {
  return (
    current.minAmount !== reference.minAmount ||
    current.maxAmount !== reference.maxAmount ||
    current.minROI !== reference.minROI ||
    current.maxROI !== reference.maxROI ||
    current.sortOrder !== reference.sortOrder ||
    current.poolType !== reference.poolType ||
    current.poolProgram !== reference.poolProgram ||
    current.minPropertyValue !== reference.minPropertyValue ||
    current.maxPropertyValue !== reference.maxPropertyValue
  );
};

export default function PoolFiltersDropdown({ onFiltersChange }: PoolFiltersDropdownProps) {
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [minROI, setMinROI] = useState('');
  const [maxROI, setMaxROI] = useState('');
  const [sortOrder, setSortOrder] = useState<PoolSortOrder>(PoolSortOrder.DESC);
  const [poolType, setPoolType] = useState('');
  const [poolProgram, setPoolProgram] = useState('');
  const [minPropertyValue, setMinPropertyValue] = useState('');
  const [maxPropertyValue, setMaxPropertyValue] = useState('');

  const [isPoolProgramOpen, setIsPoolProgramOpen] = useState(false);
  const [isPoolTypeOpen, setIsPoolTypeOpen] = useState(false);
  const [isSortOrderOpen, setIsSortOrderOpen] = useState(false);

  const [comparisonState, setComparisonState] = useState<FilterState>(defaultFilterState);

  const currentState: FilterState = useMemo(
    () => ({
      minAmount,
      maxAmount,
      minROI,
      maxROI,
      sortOrder,
      poolType,
      poolProgram,
      minPropertyValue,
      maxPropertyValue,
    }),
    [minAmount, maxAmount, minROI, maxROI, sortOrder, poolType, poolProgram, minPropertyValue, maxPropertyValue]
  );

  const hasFiltersChanged = useMemo(
    () => hasFiltersChangedFrom(currentState, comparisonState),
    [currentState, comparisonState]
  );

  const handleClearClick = () => {
    setMinAmount('');
    setMaxAmount('');
    setMinROI('');
    setMaxROI('');
    setSortOrder(PoolSortOrder.DESC);
    setPoolType('');
    setPoolProgram('');
    setMinPropertyValue('');
    setMaxPropertyValue('');
    setIsPoolProgramOpen(false);
    setIsPoolTypeOpen(false);
    setIsSortOrderOpen(false);
  };

  const handleLoadSavedFilter = (filters: PoolFilters) => {
    const state = toFilterState(filters);
    setMinAmount(state.minAmount);
    setMaxAmount(state.maxAmount);
    setMinROI(state.minROI);
    setMaxROI(state.maxROI);
    setSortOrder(state.sortOrder);
    setPoolType(state.poolType);
    setPoolProgram(state.poolProgram);
    setMinPropertyValue(state.minPropertyValue);
    setMaxPropertyValue(state.maxPropertyValue);
    setComparisonState(state);
  };

  const getCurrentFilters = useCallback((): PoolFilters => {
    const filters: PoolFilters = {};
    if (minAmount) filters.min_amount = parseFloat(minAmount);
    if (maxAmount) filters.max_amount = parseFloat(maxAmount);
    if (minROI) filters.min_roi = parseFloat(minROI);
    if (maxROI) filters.max_roi = parseFloat(maxROI);
    if (sortOrder === PoolSortOrder.ASC || sortOrder === PoolSortOrder.DESC) {
      filters.sort_order = sortOrder;
      filters.sort_by = PoolSortBy.CREATED_AT;
    }
    if (poolType === PoolType.EQUITY || poolType === PoolType.REFINANCE) {
      filters.pool_type = poolType;
    }
    if (poolProgram === LoanType.INTEREST_ONLY || poolProgram === LoanType.AMORTIZING) {
      filters.pool_program = poolProgram;
    }
    if (minPropertyValue) filters.min_property_value = parseFloat(minPropertyValue);
    if (maxPropertyValue) filters.max_property_value = parseFloat(maxPropertyValue);
    return filters;
  }, [minAmount, maxAmount, minROI, maxROI, sortOrder, poolType, poolProgram, minPropertyValue, maxPropertyValue]);

  const togglePoolProgram = () => {
    setIsPoolProgramOpen(!isPoolProgramOpen);
    setIsPoolTypeOpen(false);
    setIsSortOrderOpen(false);
  };

  const togglePoolType = () => {
    setIsPoolTypeOpen(!isPoolTypeOpen);
    setIsPoolProgramOpen(false);
    setIsSortOrderOpen(false);
  };

  const toggleSortOrder = () => {
    setIsSortOrderOpen(!isSortOrderOpen);
    setIsPoolProgramOpen(false);
    setIsPoolTypeOpen(false);
  };

  const handleApplyFilters = () => {
    onFiltersChange(getCurrentFilters());
  };

  return (
    <div className="w-full bg-white rounded-3xl border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6">
      <div className="mb-6">
        <FiltersHeader
          onLoadFilter={handleLoadSavedFilter}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearClick}
          getCurrentFilters={getCurrentFilters}
          hasFiltersChanged={hasFiltersChanged}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <MinMaxField
          label="Requested Amount"
          minValue={minAmount}
          maxValue={maxAmount}
          onMinChange={setMinAmount}
          onMaxChange={setMaxAmount}
          isCurrency={true}
        />
        <MinMaxField
          label="Interest Rate"
          minValue={minROI}
          maxValue={maxROI}
          onMinChange={setMinROI}
          onMaxChange={setMaxROI}
          isCurrency={false}
        />
        <MinMaxField
          label="Property Value"
          minValue={minPropertyValue}
          maxValue={maxPropertyValue}
          onMinChange={setMinPropertyValue}
          onMaxChange={setMaxPropertyValue}
          isCurrency={true}
        />
      </div>

      {/*Seperator*/}
      <div className="border-t border-gray-300 my-6"></div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-6 justify-center lg:items-stretch px-4 sm:px-8 lg:px-12">
        <DropDownFilterField
          label="Pool Program"
          value={poolProgram}
          onChange={(value) => setPoolProgram(value)}
          options={POOL_PROGRAM_OPTIONS}
          isOpen={isPoolProgramOpen}
          onToggle={togglePoolProgram}
        />
        <DropDownFilterField
          label="Pool Type"
          value={poolType}
          onChange={(value) => setPoolType(value)}
          options={POOL_TYPE_OPTIONS}
          isOpen={isPoolTypeOpen}
          onToggle={togglePoolType}
        />
        <DropDownFilterField
          label="Sort Order"
          value={sortOrder}
          onChange={(value) => setSortOrder(value as PoolSortOrder)}
          options={SORT_ORDER_OPTIONS}
          isOpen={isSortOrderOpen}
          onToggle={toggleSortOrder}
        />
      </div>
    </div>
  );
}
